import React, { useState, useEffect, useRef } from 'react';
import { Toast } from 'primereact/toast';
import { Message } from 'primereact/message';
import { DropdownOption, GlobalDropdownField } from '../../components/GlobalComponents/GlobalDropdownField';
import { GlobalInputField } from '../../components/GlobalComponents/GlobalInputField';
import { GlobalDateField } from '../../components/GlobalComponents/GlobalDateField';
import { GlobalButton } from '../../components/GlobalComponents/GlobalButton';
import { useOperatorProfileStore } from '../../stateManagers/stores/operatorProfileStore';
import apiClient from '../../stateManagers/apiSetup/axiosInterceptor';
import { Avatar } from 'primereact/avatar';
import { FileUpload } from 'primereact/fileupload';
import { Tooltip } from 'primereact/tooltip';

// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
  navy:       '#0e225d',
  navyMid:    '#1a3370',
  navyDark:   '#0a1a3d',
  gold:       '#fbb21f',
  goldLight:  '#ffc93c',
  goldDim:    'rgba(251,178,31,0.18)',
  goldBorder: 'rgba(251,178,31,0.35)',
  white:      '#ffffff',
  border:     '#E5E7EB',
  bg:         '#F5F7FA',
  muted:      '#9ca3af',
  sub:        '#6b7280',
  body:       '#1f2937',
};

// ── Shared section styles ─────────────────────────────────────────────────────
const sectionStyle: React.CSSProperties = {
  background: C.white, borderRadius: '16px', padding: '24px',
  marginBottom: '20px', boxShadow: '0 2px 8px rgba(14,34,93,0.06)', border: `1px solid ${C.border}`,
};
const sectionHeaderStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: '12px',
  marginBottom: '20px', paddingBottom: '14px', borderBottom: `1px solid ${C.border}`,
};
const iconBoxStyle: React.CSSProperties = {
  width: '40px', height: '40px',
  background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navyMid} 100%)`,
  borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
};

// ── Static data ────────────────────────────────────────────────────────────────
const INDIAN_STATES: DropdownOption[] = [
  { label: 'Andhra Pradesh', value: 'Andhra Pradesh' }, { label: 'Arunachal Pradesh', value: 'Arunachal Pradesh' },
  { label: 'Assam', value: 'Assam' }, { label: 'Bihar', value: 'Bihar' },
  { label: 'Chhattisgarh', value: 'Chhattisgarh' }, { label: 'Goa', value: 'Goa' },
  { label: 'Gujarat', value: 'Gujarat' }, { label: 'Haryana', value: 'Haryana' },
  { label: 'Himachal Pradesh', value: 'Himachal Pradesh' }, { label: 'Jharkhand', value: 'Jharkhand' },
  { label: 'Karnataka', value: 'Karnataka' }, { label: 'Kerala', value: 'Kerala' },
  { label: 'Madhya Pradesh', value: 'Madhya Pradesh' }, { label: 'Maharashtra', value: 'Maharashtra' },
  { label: 'Manipur', value: 'Manipur' }, { label: 'Meghalaya', value: 'Meghalaya' },
  { label: 'Mizoram', value: 'Mizoram' }, { label: 'Nagaland', value: 'Nagaland' },
  { label: 'Odisha', value: 'Odisha' }, { label: 'Punjab', value: 'Punjab' },
  { label: 'Rajasthan', value: 'Rajasthan' }, { label: 'Sikkim', value: 'Sikkim' },
  { label: 'Tamil Nadu', value: 'Tamil Nadu' }, { label: 'Telangana', value: 'Telangana' },
  { label: 'Tripura', value: 'Tripura' }, { label: 'Uttar Pradesh', value: 'Uttar Pradesh' },
  { label: 'Uttarakhand', value: 'Uttarakhand' }, { label: 'West Bengal', value: 'West Bengal' },
  { label: 'Delhi', value: 'Delhi' }, { label: 'Jammu & Kashmir', value: 'Jammu and Kashmir' },
  { label: 'Ladakh', value: 'Ladakh' }, { label: 'Chandigarh', value: 'Chandigarh' },
  { label: 'Puducherry', value: 'Puducherry' }, { label: 'Andaman & Nicobar', value: 'Andaman & Nicobar' },
  { label: 'Dadra & Nagar Haveli', value: 'Dadra and Nagar Haveli' },
  { label: 'Daman & Diu', value: 'Daman and Diu' }, { label: 'Lakshadweep', value: 'Lakshadweep' },
];

// ──────────────────────────────────────────────────────────────────────────────

const AddTeacher = () => {
  const toast = useRef<Toast>(null);
  const fileUploadRef = useRef<FileUpload>(null);

  const [loading, setLoading]                     = useState(false);
  const [error, setError]                         = useState<string | null>(null);
  const [generatedEmployeeId, setGeneratedEmployeeId] = useState<string | null>(null);
  const { profile, loading: profileLoading }      = useOperatorProfileStore();

  const [formData, setFormData] = useState({
    name: '', dateOfBirth: null as Date | null, gender: '', bloodGroup: '',
    aadharNumber: '', category: '', maritalStatus: '',
    mobileNumber: '', whatsappNumber: '', email: '',
    qualification: '', dateOfJoining: null as Date | null, experience: '',
    spouseName: '', spouseContact: '',
    emergencyContactName: '', emergencyContactNumber: '', emergencyContactRelation: '',
    wardNo: '', villageTown: '', tehsil: '', district: '', state: '', pinCode: '',
    profilePicture: null as File | null, profilePicturePreview: '' as string,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const showSuccess = (detail: string, summary = 'Success') =>
    toast.current?.show({ severity: 'success', summary, detail, life: 3200 });
  const showError = (detail: string, summary = 'Error') =>
    toast.current?.show({ severity: 'error', summary, detail, life: 4500 });

  // Dropdown options
  const genderOptions:        DropdownOption[] = [{ label: 'Male', value: 'male' }, { label: 'Female', value: 'female' }, { label: 'Other', value: 'other' }];
  const bloodGroupOptions:    DropdownOption[] = ['A+','A-','B+','B-','AB+','AB-','O+','O-','Unknown'].map(v => ({ label: v, value: v }));
  const categoryOptions:      DropdownOption[] = [{ label: 'Scheduled Caste', value: 'SC' }, { label: 'Scheduled Tribe', value: 'ST' }, { label: 'Other Backward Classes', value: 'OBC' }, { label: 'General', value: 'General' }];
  const maritalStatusOptions: DropdownOption[] = [{ label: 'Single', value: 'single' }, { label: 'Married', value: 'married' }, { label: 'Divorced', value: 'divorced' }, { label: 'Widowed', value: 'widowed' }];
  const relationOptions:      DropdownOption[] = ['Father','Mother','Spouse','Sibling','Friend','Other'].map(v => ({ label: v, value: v.toLowerCase() }));

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleProfilePictureSelect = (e: { files: File[] }) => {
    const file = e.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { showError('Image size should be less than 5MB', 'File Too Large'); return; }
    const allowed = ['image/jpeg','image/jpg','image/png','image/gif','image/webp'];
    if (!allowed.includes(file.type)) { showError('Only JPEG, PNG, GIF, WebP images are allowed', 'Invalid Format'); return; }
    setFormData(prev => ({ ...prev, profilePicture: file, profilePicturePreview: URL.createObjectURL(file) }));
  };

  const removeProfilePicture = () => {
    if (formData.profilePicturePreview) URL.revokeObjectURL(formData.profilePicturePreview);
    setFormData(prev => ({ ...prev, profilePicture: null, profilePicturePreview: '' }));
    fileUploadRef.current?.clear();
  };

  const validateForm = () => {
    const e: Record<string, string> = {};
    if (!formData.name.trim())          e.name          = 'Name is required';
    if (!formData.dateOfBirth)          e.dateOfBirth   = 'Date of birth is required';
    if (!formData.gender)               e.gender        = 'Gender is required';
    const cleanAadhar = formData.aadharNumber?.replace(/\s/g, '') || '';
    if (!cleanAadhar)                   e.aadharNumber  = 'Aadhar number is required';
    else if (cleanAadhar.length !== 12) e.aadharNumber  = 'Aadhar must be exactly 12 digits';
    if (!formData.category)             e.category      = 'Category is required';
    const cleanMobile = formData.mobileNumber?.replace(/\s/g, '') || '';
    if (!cleanMobile)                   e.mobileNumber  = 'Mobile number is required';
    else if (cleanMobile.length !== 10) e.mobileNumber  = 'Mobile must be 10 digits';
    if (!formData.email)                e.email         = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) e.email = 'Invalid email format';
    if (!formData.district.trim())      e.district      = 'District is required';
    if (!formData.state)                e.state         = 'State is required';
    if (!formData.pinCode)              e.pinCode       = 'Pin code is required';
    else if (!/^\d{6}$/.test(formData.pinCode)) e.pinCode = 'Pin code must be 6 digits';
    if (!formData.maritalStatus)        e.maritalStatus = 'Marital status is required';
    if (!formData.qualification.trim()) e.qualification = 'Qualification is required';
    if (!formData.dateOfJoining)        e.dateOfJoining = 'Date of joining is required';
    if (formData.whatsappNumber && formData.whatsappNumber.replace(/\s/g,'').length !== 10) e.whatsappNumber = 'WhatsApp must be 10 digits';
    if (formData.spouseContact && formData.spouseContact.replace(/\s/g,'').length !== 10) e.spouseContact = 'Spouse contact must be 10 digits';
    if (formData.emergencyContactNumber && formData.emergencyContactNumber.replace(/\s/g,'').length !== 10) e.emergencyContactNumber = 'Emergency contact must be 10 digits';
    setErrors(e);
    if (Object.keys(e).length) showError('Please fill all required fields correctly', 'Validation Failed');
    return Object.keys(e).length === 0;
  };

  const resetForm = () => {
    setFormData({ name: '', dateOfBirth: null, gender: '', bloodGroup: '', aadharNumber: '', category: '', maritalStatus: '', mobileNumber: '', whatsappNumber: '', email: '', qualification: '', dateOfJoining: null, experience: '', spouseName: '', spouseContact: '', emergencyContactName: '', emergencyContactNumber: '', emergencyContactRelation: '', wardNo: '', villageTown: '', tehsil: '', district: '', state: '', pinCode: '', profilePicture: null, profilePicturePreview: '' });
    setErrors({});
    fileUploadRef.current?.clear();
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    if (!profile?.institute_id || !profile?.branch_id) { showError('Institute or Branch information is missing.', 'Configuration Error'); return; }
    setLoading(true); setError(null); setGeneratedEmployeeId(null);
    try {
      const fd = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        if (key === 'profilePicture' || key === 'profilePicturePreview') return;
        if (value instanceof Date) fd.append(key, value.toISOString());
        else if (value !== null && value !== undefined && value !== '') {
          if (['mobileNumber','whatsappNumber','spouseContact','emergencyContactNumber'].includes(key))
            fd.append(key, String(value).replace(/\s/g, ''));
          else fd.append(key, String(value));
        }
      });
      if (formData.profilePicture) fd.append('profilePicture', formData.profilePicture);
      fd.append('institute_id', profile.institute_id);
      fd.append('branch_id', profile.branch_id);
      const response = await apiClient.post('/v1/teachers/add-teacher', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (response.data.success) {
        const empId = response.data.data?.employee_id;
        setGeneratedEmployeeId(empId ?? null);
        showSuccess(empId ? `Teacher registered! Employee ID: ${empId}` : 'Teacher registered successfully!', 'Teacher Added');
        if (formData.profilePicturePreview) URL.revokeObjectURL(formData.profilePicturePreview);
        setTimeout(() => { resetForm(); setGeneratedEmployeeId(null); }, 3400);
      } else { showError(response.data.message || 'Failed to add teacher'); }
    } catch (err: any) {
      showError(err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to add teacher. Please try again.');
    } finally { setLoading(false); }
  };

  const handleCancel = () => {
    if (formData.profilePicturePreview) URL.revokeObjectURL(formData.profilePicturePreview);
    resetForm(); setError(null); setGeneratedEmployeeId(null);
  };

  useEffect(() => {
    return () => { if (formData.profilePicturePreview) URL.revokeObjectURL(formData.profilePicturePreview); };
  }, [formData.profilePicturePreview]);

  // ── Loading / no profile ───────────────────────────────────────────────────
  if (profileLoading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px', flexDirection: 'column', gap: '16px' }}>
      <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem', color: C.navy }} />
      <p style={{ color: C.sub, fontSize: '0.9rem' }}>Loading profile information...</p>
    </div>
  );

  if (!profile) return (
    <div style={{ padding: '24px' }}>
      <Message severity="warn" text="Unable to load institute and branch information. Please refresh the page." style={{ fontSize: '0.9rem' }} />
    </div>
  );

  return (
    <div style={{ padding: '0', margin: '0 auto' }}>
      <Toast ref={toast} position="top-right" />

      {/* ── Hero Header ────────────────────────────────────────────────────── */}
      <div style={{
        background: `linear-gradient(180deg, ${C.navy} 0%, ${C.navyMid} 50%, ${C.navyDark} 100%)`,
        borderRadius: '20px', padding: '32px', marginBottom: '24px', color: C.white,
        position: 'relative', overflow: 'hidden', boxShadow: '0 10px 40px rgba(14,34,93,0.25)',
      }}>
        <div style={{ position: 'absolute', top: '-60px', right: '-60px', width: '220px', height: '220px', background: C.goldDim, borderRadius: '50%', filter: 'blur(50px)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: '-40px', left: '30%', width: '140px', height: '140px', background: 'rgba(251,178,31,0.05)', borderRadius: '50%', filter: 'blur(40px)', pointerEvents: 'none' }} />

        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '4px 12px', background: C.goldDim, border: `1px solid ${C.goldBorder}`, borderRadius: '20px', marginBottom: '12px', fontSize: '0.68rem', fontWeight: '700', letterSpacing: '0.8px', textTransform: 'uppercase', color: C.gold }}>
            <i className="pi pi-user-plus" style={{ fontSize: '0.7rem' }} />
            Teacher Management
          </div>
          <h1 style={{ margin: '0 0 8px 0', fontSize: '1.75rem', fontWeight: '700', color: C.white, letterSpacing: '-0.5px' }}>Add New Teacher</h1>
          <p style={{ margin: 0, color: 'rgba(255,255,255,0.6)', fontSize: '0.9rem' }}>
            Fill in the teacher details to register a new teacher. Employee ID is auto-generated.
          </p>
        </div>
      </div>

      {error && <Message severity="error" text={error} style={{ marginBottom: '20px', fontSize: '0.9rem' }} />}

      {/* ── Employee ID success banner ─────────────────────────────────────── */}
      {generatedEmployeeId && (
        <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '14px', padding: '16px 20px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ width: '38px', height: '38px', background: 'linear-gradient(135deg,#166534,#22c55e)', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <i className="pi pi-check" style={{ color: C.white, fontSize: '1rem' }} />
          </div>
          <div>
            <p style={{ margin: '0 0 2px 0', fontSize: '0.68rem', color: '#166534', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.6px' }}>Employee ID Generated</p>
            <p style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700', color: '#15803d', fontFamily: 'monospace' }}>{generatedEmployeeId}</p>
          </div>
        </div>
      )}

      {/* ── Personal Information ──────────────────────────────────────────── */}
      <div style={sectionStyle}>
        <div style={sectionHeaderStyle}>
          <div style={iconBoxStyle}><i className="pi pi-user" style={{ color: C.gold, fontSize: '1.1rem' }} /></div>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.05rem', fontWeight: '700', color: C.navy }}>Personal Information</h2>
            <p style={{ margin: '2px 0 0 0', fontSize: '0.75rem', color: C.muted }}>Basic personal and identification details</p>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '32px', alignItems: 'start' }}>
          {/* Profile Picture Upload */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', padding: '16px', background: C.bg, borderRadius: '14px', border: `2px dashed ${C.goldBorder}`, minWidth: '130px' }}>
            <div style={{ position: 'relative' }}>
              <Avatar
                image={formData.profilePicturePreview || undefined}
                size="xlarge" shape="circle"
                icon={!formData.profilePicturePreview ? 'pi pi-user' : undefined}
                style={{ width: '100px', height: '100px', border: `3px solid ${C.goldBorder}`, backgroundColor: formData.profilePicturePreview ? 'transparent' : C.goldDim, color: C.gold, boxShadow: '0 4px 16px rgba(14,34,93,0.12)' }}
              />
              {formData.profilePicturePreview && (
                <>
                  <Tooltip target=".remove-btn" />
                  <button
                    className="remove-btn"
                    onClick={removeProfilePicture}
                    title="Remove photo"
                    style={{ position: 'absolute', bottom: '0', right: '0', width: '28px', height: '28px', background: C.white, border: '1px solid #fecaca', borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}
                  >
                    <i className="pi pi-trash" style={{ color: '#dc2626', fontSize: '0.75rem' }} />
                  </button>
                </>
              )}
            </div>

            <button
              onClick={() => fileUploadRef.current?.getInput()?.click()}
              disabled={loading}
              style={{ width: '100%', padding: '8px 12px', background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navyMid} 100%)`, color: C.white, border: 'none', borderRadius: '8px', fontSize: '0.75rem', fontWeight: '700', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.6 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', transition: 'all 0.2s ease', boxShadow: '0 2px 6px rgba(14,34,93,0.2)' }}
              onMouseEnter={e => { if (!loading) { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(14,34,93,0.3)'; } }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 6px rgba(14,34,93,0.2)'; }}
            >
              <i className="pi pi-upload" style={{ fontSize: '0.72rem' }} />
              Upload Photo
            </button>
            <FileUpload
              ref={fileUploadRef} name="profilePicture"
              accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
              maxFileSize={5000000} auto mode="basic" customUpload
              uploadHandler={handleProfilePictureSelect} disabled={loading} style={{ display: 'none' }}
            />
            <p style={{ margin: '0', fontSize: '0.63rem', color: C.muted, textAlign: 'center', lineHeight: '1.4' }}>
              PNG, JPG, GIF, WebP<br />Max 5 MB
            </p>
          </div>

          {/* Personal Fields */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
            <GlobalInputField label="Name of Teacher" required value={formData.name} onChange={e => handleInputChange('name', e.target.value)} placeholder="Enter full name" icon="pi pi-user" error={errors.name} />
            <GlobalDateField label="Date of Birth" required value={formData.dateOfBirth} onChange={val => handleInputChange('dateOfBirth', val)} placeholder="Select date" error={errors.dateOfBirth} />
            <GlobalDropdownField label="Gender" required value={formData.gender} onChange={val => handleInputChange('gender', val)} options={genderOptions} placeholder="Select gender" icon="pi pi-mars" error={errors.gender} />
            <GlobalDropdownField label="Blood Group" value={formData.bloodGroup} onChange={val => handleInputChange('bloodGroup', val)} options={bloodGroupOptions} placeholder="Select blood group" icon="pi pi-heart" />
            <GlobalInputField label="Aadhar Number" required value={formData.aadharNumber} onChange={e => handleInputChange('aadharNumber', e.target.value)} placeholder="Enter 12-digit Aadhar" maxLength={12} icon="pi pi-id-card" error={errors.aadharNumber} />
            <GlobalDropdownField label="Category" required value={formData.category} onChange={val => handleInputChange('category', val)} options={categoryOptions} placeholder="Select category" icon="pi pi-tag" error={errors.category} />
            <GlobalDropdownField label="Marital Status" required value={formData.maritalStatus} onChange={val => handleInputChange('maritalStatus', val)} options={maritalStatusOptions} placeholder="Select marital status" icon="pi pi-users" error={errors.maritalStatus} />
          </div>
        </div>
      </div>

      {/* ── Contact Information ───────────────────────────────────────────── */}
      <div style={sectionStyle}>
        <div style={sectionHeaderStyle}>
          <div style={iconBoxStyle}><i className="pi pi-phone" style={{ color: C.gold, fontSize: '1.1rem' }} /></div>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.05rem', fontWeight: '700', color: C.navy }}>Contact Information</h2>
            <p style={{ margin: '2px 0 0 0', fontSize: '0.75rem', color: C.muted }}>Phone, email and communication details</p>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
          <GlobalInputField label="Mobile Number" required value={formData.mobileNumber} onChange={e => handleInputChange('mobileNumber', e.target.value)} placeholder="Enter mobile number" maxLength={10} icon="pi pi-mobile" error={errors.mobileNumber} />
          <GlobalInputField label="WhatsApp Number" value={formData.whatsappNumber} onChange={e => handleInputChange('whatsappNumber', e.target.value)} placeholder="Enter WhatsApp number" maxLength={10} icon="pi pi-whatsapp" error={errors.whatsappNumber} />
          <GlobalInputField label="Email ID" type="email" required value={formData.email} onChange={e => handleInputChange('email', e.target.value)} placeholder="Enter email address" icon="pi pi-envelope" error={errors.email} />
        </div>
      </div>

      {/* ── Employment Details ────────────────────────────────────────────── */}
      <div style={sectionStyle}>
        <div style={sectionHeaderStyle}>
          <div style={iconBoxStyle}><i className="pi pi-briefcase" style={{ color: C.gold, fontSize: '1.1rem' }} /></div>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.05rem', fontWeight: '700', color: C.navy }}>Employment Details</h2>
            <p style={{ margin: '2px 0 0 0', fontSize: '0.75rem', color: C.muted }}>
              Employee ID is auto-generated on submit (e.g. HCI-JK-EMP-2026-0001)
            </p>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
          {/* Employee ID — read-only */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label style={{ fontSize: '0.75rem', fontWeight: '700', color: C.muted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Employee ID</label>
            <div style={{ padding: '11px 14px', background: C.bg, borderRadius: '8px', border: `1px dashed ${C.border}`, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <i className="pi pi-id-card" style={{ color: C.muted, fontSize: '0.9rem' }} />
              {generatedEmployeeId
                ? <span style={{ fontSize: '0.85rem', fontWeight: '700', color: C.navy, fontFamily: 'monospace' }}>{generatedEmployeeId}</span>
                : <span style={{ fontSize: '0.82rem', color: C.muted, fontStyle: 'italic' }}>Auto-generated after submit</span>}
            </div>
          </div>
          <GlobalInputField label="Qualification" required value={formData.qualification} onChange={e => handleInputChange('qualification', e.target.value)} placeholder="e.g., B.Ed, M.Ed, B.Sc" icon="pi pi-graduation-cap" error={errors.qualification} />
          <GlobalDateField label="Date of Joining" required value={formData.dateOfJoining} onChange={val => handleInputChange('dateOfJoining', val)} placeholder="Select date" error={errors.dateOfJoining} />
          <GlobalInputField label="Experience" value={formData.experience} onChange={e => handleInputChange('experience', e.target.value)} placeholder="e.g., 5 years, 10 years" icon="pi pi-chart-line" />
        </div>
      </div>

      {/* ── Spouse & Emergency Contact ────────────────────────────────────── */}
      <div style={sectionStyle}>
        <div style={sectionHeaderStyle}>
          <div style={iconBoxStyle}><i className="pi pi-users" style={{ color: C.gold, fontSize: '1.1rem' }} /></div>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.05rem', fontWeight: '700', color: C.navy }}>Spouse & Emergency Contact</h2>
            <p style={{ margin: '2px 0 0 0', fontSize: '0.75rem', color: C.muted }}>Family and emergency contact information</p>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
          <GlobalInputField label="Spouse Name" value={formData.spouseName} onChange={e => handleInputChange('spouseName', e.target.value)} placeholder="Enter spouse name (if married)" icon="pi pi-user" />
          <GlobalInputField label="Spouse Contact" value={formData.spouseContact} onChange={e => handleInputChange('spouseContact', e.target.value)} placeholder="Enter spouse contact" maxLength={10} icon="pi pi-phone" error={errors.spouseContact} />
          <GlobalInputField label="Emergency Contact Name" value={formData.emergencyContactName} onChange={e => handleInputChange('emergencyContactName', e.target.value)} placeholder="Enter emergency contact name" icon="pi pi-user" />
          <GlobalInputField label="Emergency Contact Number" value={formData.emergencyContactNumber} onChange={e => handleInputChange('emergencyContactNumber', e.target.value)} placeholder="Enter emergency contact number" maxLength={10} icon="pi pi-phone" error={errors.emergencyContactNumber} />
          <GlobalDropdownField label="Relation" value={formData.emergencyContactRelation} onChange={val => handleInputChange('emergencyContactRelation', val)} options={relationOptions} placeholder="Select relation" icon="pi pi-users" />
        </div>
      </div>

      {/* ── Address ───────────────────────────────────────────────────────── */}
      <div style={sectionStyle}>
        <div style={sectionHeaderStyle}>
          <div style={iconBoxStyle}><i className="pi pi-map-marker" style={{ color: C.gold, fontSize: '1.1rem' }} /></div>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.05rem', fontWeight: '700', color: C.navy }}>Address</h2>
            <p style={{ margin: '2px 0 0 0', fontSize: '0.75rem', color: C.muted }}>Residential address details</p>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
          <GlobalDropdownField label="State/UT" required value={formData.state} onChange={val => handleInputChange('state', val)} options={INDIAN_STATES} placeholder="Select state/UT" icon="pi pi-globe" error={errors.state} filter />
          <GlobalInputField label="District" required value={formData.district} onChange={e => handleInputChange('district', e.target.value)} placeholder="Enter district" icon="pi pi-map-marker" error={errors.district} />
          <GlobalInputField label="Tehsil" value={formData.tehsil} onChange={e => handleInputChange('tehsil', e.target.value)} placeholder="Enter tehsil" icon="pi pi-map" />
          <GlobalInputField label="Ward No" value={formData.wardNo} onChange={e => handleInputChange('wardNo', e.target.value)} placeholder="Enter ward number" icon="pi pi-map" />
          <GlobalInputField label="Village/Town" value={formData.villageTown} onChange={e => handleInputChange('villageTown', e.target.value)} placeholder="Enter village/town" icon="pi pi-home" />
          <GlobalInputField label="Pin Code" required value={formData.pinCode} onChange={e => handleInputChange('pinCode', e.target.value)} placeholder="Enter 6-digit pin code" maxLength={6} icon="pi pi-inbox" error={errors.pinCode} />
        </div>
      </div>

      {/* ── Action Buttons ────────────────────────────────────────────────── */}
      <div style={{ background: C.white, borderRadius: '16px', padding: '24px', boxShadow: '0 2px 8px rgba(14,34,93,0.06)', border: `1px solid ${C.border}`, display: 'flex', gap: '12px', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap' }}>
        {/* Primary — gold */}
        <button
          onClick={handleSubmit}
          disabled={loading}
          style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '13px 32px', background: loading ? C.muted : C.gold, border: 'none', borderRadius: '12px', color: loading ? C.white : C.navy, fontSize: '0.92rem', fontWeight: '700', cursor: loading ? 'not-allowed' : 'pointer', boxShadow: loading ? 'none' : '0 4px 12px rgba(251,178,31,0.4)', transition: 'all 0.2s ease' }}
          onMouseEnter={e => { if (!loading) { e.currentTarget.style.background = C.goldLight; e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(251,178,31,0.5)'; } }}
          onMouseLeave={e => { if (!loading) { e.currentTarget.style.background = C.gold; e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(251,178,31,0.4)'; } }}
        >
          <i className={loading ? 'pi pi-spin pi-spinner' : 'pi pi-check'} style={{ fontSize: '0.92rem' }} />
          {loading ? 'Adding Teacher...' : 'Add Teacher'}
        </button>

        {/* Secondary — navy outlined */}
        <button
          onClick={handleCancel}
          disabled={loading}
          style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '13px 28px', background: 'transparent', border: `1.5px solid ${C.navy}`, borderRadius: '12px', color: C.navy, fontSize: '0.92rem', fontWeight: '600', cursor: loading ? 'not-allowed' : 'pointer', transition: 'all 0.2s ease', opacity: loading ? 0.5 : 1 }}
          onMouseEnter={e => { if (!loading) { e.currentTarget.style.background = C.navy; (e.currentTarget as HTMLButtonElement).style.color = C.white; } }}
          onMouseLeave={e => { if (!loading) { e.currentTarget.style.background = 'transparent'; (e.currentTarget as HTMLButtonElement).style.color = C.navy; } }}
        >
          <i className="pi pi-times" style={{ fontSize: '0.92rem' }} />
          Cancel
        </button>
      </div>
    </div>
  );
};

export default AddTeacher;


// import React, { useState, useEffect, useRef } from 'react';
// import { Toast } from 'primereact/toast';
// import { Message } from 'primereact/message';
// import { DropdownOption, GlobalDropdownField } from '../../components/GlobalComponents/GlobalDropdownField';
// import { GlobalInputField } from '../../components/GlobalComponents/GlobalInputField';
// import { GlobalDateField } from '../../components/GlobalComponents/GlobalDateField';
// import { GlobalButton } from '../../components/GlobalComponents/GlobalButton';
// import { useOperatorProfileStore } from '../../stateManagers/stores/operatorProfileStore';
// import apiClient from '../../stateManagers/apiSetup/axiosInterceptor';
// import { Avatar } from 'primereact/avatar';
// import { FileUpload } from 'primereact/fileupload';
// import { Button } from 'primereact/button';
// import { Tooltip } from 'primereact/tooltip';

// // ── Static data ────────────────────────────────────────────────────────────────

// const INDIAN_STATES: DropdownOption[] = [
//   { label: 'Andhra Pradesh', value: 'Andhra Pradesh' },
//   { label: 'Arunachal Pradesh', value: 'Arunachal Pradesh' },
//   { label: 'Assam', value: 'Assam' },
//   { label: 'Bihar', value: 'Bihar' },
//   { label: 'Chhattisgarh', value: 'Chhattisgarh' },
//   { label: 'Goa', value: 'Goa' },
//   { label: 'Gujarat', value: 'Gujarat' },
//   { label: 'Haryana', value: 'Haryana' },
//   { label: 'Himachal Pradesh', value: 'Himachal Pradesh' },
//   { label: 'Jharkhand', value: 'Jharkhand' },
//   { label: 'Karnataka', value: 'Karnataka' },
//   { label: 'Kerala', value: 'Kerala' },
//   { label: 'Madhya Pradesh', value: 'Madhya Pradesh' },
//   { label: 'Maharashtra', value: 'Maharashtra' },
//   { label: 'Manipur', value: 'Manipur' },
//   { label: 'Meghalaya', value: 'Meghalaya' },
//   { label: 'Mizoram', value: 'Mizoram' },
//   { label: 'Nagaland', value: 'Nagaland' },
//   { label: 'Odisha', value: 'Odisha' },
//   { label: 'Punjab', value: 'Punjab' },
//   { label: 'Rajasthan', value: 'Rajasthan' },
//   { label: 'Sikkim', value: 'Sikkim' },
//   { label: 'Tamil Nadu', value: 'Tamil Nadu' },
//   { label: 'Telangana', value: 'Telangana' },
//   { label: 'Tripura', value: 'Tripura' },
//   { label: 'Uttar Pradesh', value: 'Uttar Pradesh' },
//   { label: 'Uttarakhand', value: 'Uttarakhand' },
//   { label: 'West Bengal', value: 'West Bengal' },
//   // Union Territories
//   { label: 'Delhi', value: 'Delhi' },
//   { label: 'Jammu & Kashmir', value: 'Jammu and Kashmir' },
//   { label: 'Ladakh', value: 'Ladakh' },
//   { label: 'Chandigarh', value: 'Chandigarh' },
//   { label: 'Puducherry', value: 'Puducherry' },
//   { label: 'Andaman & Nicobar', value: 'Andaman & Nicobar' },
//   { label: 'Dadra & Nagar Haveli', value: 'Dadra and Nagar Haveli' },
//   { label: 'Daman & Diu', value: 'Daman and Diu' },
//   { label: 'Lakshadweep', value: 'Lakshadweep' },
// ];

// // ──────────────────────────────────────────────────────────────────────────────

// const AddTeacher = () => {
//   const toast = useRef<Toast>(null);

//   const [loading, setLoading] = useState(false);
//   const [error, setError] = useState<string | null>(null);
//   const [generatedEmployeeId, setGeneratedEmployeeId] = useState<string | null>(null);
//   const { profile, loading: profileLoading } = useOperatorProfileStore();

//   const [formData, setFormData] = useState({
//     // Personal Information
//     name: '',
//     dateOfBirth: null as Date | null,
//     gender: '',
//     bloodGroup: '',
//     aadharNumber: '',
//     category: '',
//     maritalStatus: '',

//     // Contact Information
//     mobileNumber: '',
//     whatsappNumber: '',
//     email: '',

//     // Employment Details
//     qualification: '',
//     dateOfJoining: null as Date | null,
//     experience: '',

//     // Spouse Details
//     spouseName: '',
//     spouseContact: '',

//     // Emergency Contact
//     emergencyContactName: '',
//     emergencyContactNumber: '',
//     emergencyContactRelation: '',

//     // Address
//     wardNo: '',
//     villageTown: '',
//     tehsil: '',
//     district: '',
//     state: '',
//     pinCode: '',

//     // Profile Picture
//     profilePicture: null as File | null,
//     profilePicturePreview: '' as string,
//   });

//   const [errors, setErrors] = useState<Record<string, string>>({});
//   const fileUploadRef = useRef<FileUpload>(null);

//   // Toast Helpers
//   const showSuccess = (detail: string, summary = 'Success') => {
//     toast.current?.show({ severity: 'success', summary, detail, life: 3200 });
//   };

//   const showError = (detail: string, summary = 'Error') => {
//     toast.current?.show({ severity: 'error', summary, detail, life: 4500 });
//   };

//   // Dropdown Options
//   const genderOptions: DropdownOption[] = [
//     { label: 'Male', value: 'male' },
//     { label: 'Female', value: 'female' },
//     { label: 'Other', value: 'other' },
//   ];

//   const bloodGroupOptions: DropdownOption[] = [
//     { label: 'A+', value: 'A+' }, { label: 'A-', value: 'A-' },
//     { label: 'B+', value: 'B+' }, { label: 'B-', value: 'B-' },
//     { label: 'AB+', value: 'AB+' }, { label: 'AB-', value: 'AB-' },
//     { label: 'O+', value: 'O+' }, { label: 'O-', value: 'O-' },
//     { label: 'Unknown', value: 'unknown' },
//   ];

//   const categoryOptions: DropdownOption[] = [
//     { label: 'Scheduled Caste', value: 'SC' },
//     { label: 'Scheduled Tribe', value: 'ST' },
//     { label: 'Other Backward Classes', value: 'OBC' },
//     { label: 'General', value: 'General' },
//   ];

//   const maritalStatusOptions: DropdownOption[] = [
//     { label: 'Single', value: 'single' },
//     { label: 'Married', value: 'married' },
//     { label: 'Divorced', value: 'divorced' },
//     { label: 'Widowed', value: 'widowed' },
//   ];

//   const relationOptions: DropdownOption[] = [
//     { label: 'Father', value: 'father' }, { label: 'Mother', value: 'mother' },
//     { label: 'Spouse', value: 'spouse' }, { label: 'Sibling', value: 'sibling' },
//     { label: 'Friend', value: 'friend' }, { label: 'Other', value: 'other' },
//   ];

//   // Handlers
//   const handleInputChange = (field: string, value: any) => {
//     setFormData(prev => ({ ...prev, [field]: value }));
//     if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
//   };

//   const handleProfilePictureSelect = (e: { files: File[] }) => {
//     const file = e.files[0];
//     if (file) {
//       if (file.size > 5 * 1024 * 1024) {
//         showError('Image size should be less than 5MB', 'File Too Large');
//         return;
//       }
//       const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
//       if (!allowedTypes.includes(file.type)) {
//         showError('Only JPEG, PNG, GIF, WebP images are allowed', 'Invalid Format');
//         return;
//       }
//       const previewUrl = URL.createObjectURL(file);
//       setFormData(prev => ({ ...prev, profilePicture: file, profilePicturePreview: previewUrl }));
//     }
//   };

//   const removeProfilePicture = () => {
//     if (formData.profilePicturePreview) URL.revokeObjectURL(formData.profilePicturePreview);
//     setFormData(prev => ({ ...prev, profilePicture: null, profilePicturePreview: '' }));
//     if (fileUploadRef.current) fileUploadRef.current.clear();
//   };

//   const validateForm = () => {
//     const newErrors: Record<string, string> = {};

//     if (!formData.name.trim())        newErrors.name         = 'Name is required';
//     if (!formData.dateOfBirth)        newErrors.dateOfBirth  = 'Date of birth is required';
//     if (!formData.gender)             newErrors.gender        = 'Gender is required';

//     const cleanAadhar = formData.aadharNumber?.replace(/\s/g, '') || '';
//     if (!cleanAadhar)                 newErrors.aadharNumber = 'Aadhar number is required';
//     else if (cleanAadhar.length !== 12) newErrors.aadharNumber = 'Aadhar must be exactly 12 digits';

//     if (!formData.category)           newErrors.category      = 'Category is required';

//     const cleanMobile = formData.mobileNumber?.replace(/\s/g, '') || '';
//     if (!cleanMobile)                 newErrors.mobileNumber = 'Mobile number is required';
//     else if (cleanMobile.length !== 10) newErrors.mobileNumber = 'Mobile must be 10 digits';

//     if (!formData.email)              newErrors.email         = 'Email is required';
//     else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Invalid email format';

//     if (!formData.district.trim())    newErrors.district      = 'District is required';
//     if (!formData.state)              newErrors.state         = 'State is required';

//     if (!formData.pinCode)            newErrors.pinCode       = 'Pin code is required';
//     else if (!/^\d{6}$/.test(formData.pinCode)) newErrors.pinCode = 'Pin code must be 6 digits';

//     if (!formData.maritalStatus)      newErrors.maritalStatus = 'Marital status is required';
//     if (!formData.qualification.trim()) newErrors.qualification = 'Qualification is required';
//     if (!formData.dateOfJoining)      newErrors.dateOfJoining = 'Date of joining is required';

//     if (formData.whatsappNumber) {
//       const clean = formData.whatsappNumber.replace(/\s/g, '');
//       if (clean.length !== 10) newErrors.whatsappNumber = 'WhatsApp must be 10 digits';
//     }
//     if (formData.spouseContact) {
//       const clean = formData.spouseContact.replace(/\s/g, '');
//       if (clean.length !== 10) newErrors.spouseContact = 'Spouse contact must be 10 digits';
//     }
//     if (formData.emergencyContactNumber) {
//       const clean = formData.emergencyContactNumber.replace(/\s/g, '');
//       if (clean.length !== 10) newErrors.emergencyContactNumber = 'Emergency contact must be 10 digits';
//     }

//     setErrors(newErrors);
//     const isValid = Object.keys(newErrors).length === 0;
//     if (!isValid) showError('Please fill all required fields correctly', 'Validation Failed');
//     return isValid;
//   };

//   const resetForm = () => {
//     setFormData({
//       name: '', dateOfBirth: null, gender: '', bloodGroup: '',
//       aadharNumber: '', category: '', maritalStatus: '',
//       mobileNumber: '', whatsappNumber: '', email: '',
//       qualification: '', dateOfJoining: null, experience: '',
//       spouseName: '', spouseContact: '',
//       emergencyContactName: '', emergencyContactNumber: '', emergencyContactRelation: '',
//       wardNo: '', villageTown: '', tehsil: '', district: '', state: '', pinCode: '',
//       profilePicture: null, profilePicturePreview: '',
//     });
//     setErrors({});
//     if (fileUploadRef.current) fileUploadRef.current.clear();
//   };

//   const handleSubmit = async () => {
//     if (!validateForm()) return;

//     if (!profile?.institute_id || !profile?.branch_id) {
//       showError('Institute or Branch information is missing. Please try again.', 'Configuration Error');
//       return;
//     }

//     setLoading(true);
//     setError(null);
//     setGeneratedEmployeeId(null);

//     try {
//       const formDataToSend = new FormData();

//       Object.entries(formData).forEach(([key, value]) => {
//         if (key !== 'profilePicture' && key !== 'profilePicturePreview') {
//           if (value instanceof Date) {
//             formDataToSend.append(key, value.toISOString());
//           } else if (value !== null && value !== undefined && value !== '') {
//             if (['mobileNumber', 'whatsappNumber', 'spouseContact', 'emergencyContactNumber'].includes(key)) {
//               formDataToSend.append(key, String(value).replace(/\s/g, ''));
//             } else {
//               formDataToSend.append(key, String(value));
//             }
//           }
//         }
//       });

//       if (formData.profilePicture) formDataToSend.append('profilePicture', formData.profilePicture);

//       formDataToSend.append('institute_id', profile.institute_id);
//       formDataToSend.append('branch_id', profile.branch_id);

//       const response = await apiClient.post('/v1/teachers/add-teacher', formDataToSend, {
//         headers: { 'Content-Type': 'multipart/form-data' },
//       });

//       if (response.data.success) {
//         const empId = response.data.data?.employee_id;
//         setGeneratedEmployeeId(empId ?? null);

//         showSuccess(
//           empId ? `Teacher registered! Employee ID: ${empId}` : 'Teacher has been successfully registered!',
//           'Teacher Added',
//         );

//         if (formData.profilePicturePreview) URL.revokeObjectURL(formData.profilePicturePreview);

//         setTimeout(() => {
//           resetForm();
//           setGeneratedEmployeeId(null);
//         }, 3400);
//       } else {
//         showError(response.data.message || 'Failed to add teacher');
//       }
//     } catch (err: any) {
//       const errorMessage =
//         err.response?.data?.message ||
//         err.response?.data?.error ||
//         err.message ||
//         'Failed to add teacher. Please try again.';
//       showError(errorMessage);
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleCancel = () => {
//     if (formData.profilePicturePreview) URL.revokeObjectURL(formData.profilePicturePreview);
//     resetForm();
//     setError(null);
//     setGeneratedEmployeeId(null);
//   };

//   useEffect(() => {
//     return () => {
//       if (formData.profilePicturePreview) URL.revokeObjectURL(formData.profilePicturePreview);
//     };
//   }, [formData.profilePicturePreview]);

//   // ── Styles ─────────────────────────────────────────────────────────────────
//   const sectionStyle = {
//     background: 'white', borderRadius: '16px', padding: '24px',
//     marginBottom: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', border: '1px solid #f0f0f0',
//   };
//   const sectionHeaderStyle = {
//     display: 'flex', alignItems: 'center', gap: '12px',
//     marginBottom: '20px', paddingBottom: '12px', borderBottom: '2px solid #f0f0f0',
//   };
//   const iconBoxStyle = {
//     width: '40px', height: '40px',
//     background: 'linear-gradient(135deg, #0f3d3e 0%, #1a5555 100%)',
//     borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center',
//   };

//   if (profileLoading) {
//     return (
//       <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px', flexDirection: 'column', gap: '16px' }}>
//         <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem', color: '#0f3d3e' }}></i>
//         <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>Loading profile information...</p>
//       </div>
//     );
//   }

//   if (!profile) {
//     return (
//       <div style={{ padding: '24px' }}>
//         <Message severity="warn" text="Unable to load institute and branch information. Please refresh the page." style={{ fontSize: '0.9rem' }} />
//       </div>
//     );
//   }

//   return (
//     <div style={{ padding: '0', margin: '0 auto' }}>
//       <Toast ref={toast} position="top-right" />

//       {/* Header */}
//       <div style={{
//         background: 'linear-gradient(135deg, #0f3d3e 0%, #1a5555 50%, #0a2f2f 100%)',
//         borderRadius: '20px', padding: '32px', marginBottom: '24px', color: 'white',
//         position: 'relative', overflow: 'hidden', boxShadow: '0 10px 40px rgba(15, 61, 62, 0.3)',
//       }}>
//         <div style={{ position: 'absolute', top: '-50px', right: '-50px', width: '200px', height: '200px', background: 'rgba(212, 241, 126, 0.1)', borderRadius: '50%', filter: 'blur(40px)' }} />
//         <div style={{ position: 'relative', zIndex: 1 }}>
//           <div style={{ display: 'inline-block', padding: '5px 12px', background: 'rgba(212, 241, 126, 0.2)', borderRadius: '20px', marginBottom: '10px', fontSize: '0.7rem', fontWeight: '600', letterSpacing: '0.5px', textTransform: 'uppercase' }}>
//             Teacher Management
//           </div>
//           <h1 style={{ margin: '0 0 8px 0', fontSize: '1.7rem', fontWeight: '700', color: 'white', letterSpacing: '-0.5px' }}>Add New Teacher</h1>
//           <p style={{ margin: '0', color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem' }}>
//             Fill in the teacher details to register a new teacher. Employee ID is auto-generated.
//           </p>
//         </div>
//       </div>

//       {error && <Message severity="error" text={error} style={{ marginBottom: '20px', fontSize: '0.9rem' }} />}

//       {/* Auto-generated Employee ID banner */}
//       {generatedEmployeeId && (
//         <div style={{
//           background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '12px',
//           padding: '16px 20px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '12px',
//         }}>
//           <i className="pi pi-check-circle" style={{ color: '#16a34a', fontSize: '1.4rem' }} />
//           <div>
//             <p style={{ margin: 0, fontSize: '0.75rem', color: '#166534', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Employee ID Generated</p>
//             <p style={{ margin: '2px 0 0 0', fontSize: '1.1rem', fontWeight: '700', color: '#15803d', fontFamily: 'monospace' }}>{generatedEmployeeId}</p>
//           </div>
//         </div>
//       )}

//       {/* ── Personal Information ── */}
//       <div style={sectionStyle}>
//         <div style={sectionHeaderStyle}>
//           <div style={iconBoxStyle}>
//             <i className="pi pi-user" style={{ color: '#d4f17e', fontSize: '1.1rem' }}></i>
//           </div>
//           <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700', color: '#0f3d3e' }}>Personal Information</h2>
//         </div>

//         <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '32px', alignItems: 'center' }}>
//           {/* Profile Picture Upload */}
//           <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', padding: '10px 14px 0px 14px', background: '#f9fafb', borderRadius: '12px', border: '2px dashed #e5e7eb', minWidth: '120px' }}>
//             <div style={{ position: 'relative' }}>
//               <Avatar
//                 image={formData.profilePicturePreview || undefined}
//                 size="xlarge"
//                 shape="circle"
//                 icon={!formData.profilePicturePreview ? 'pi pi-user' : undefined}
//                 style={{ width: '100px', height: '100px', border: '3px solid #fff', backgroundColor: '#e5e7eb', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
//               />
//               {formData.profilePicturePreview && (
//                 <>
//                   <Tooltip target=".remove-btn" />
//                   <Button
//                     className="remove-btn" icon="pi pi-trash" rounded text severity="danger"
//                     style={{ position: 'absolute', bottom: '0', right: '0', background: 'white', boxShadow: '0 2px 8px rgba(0,0,0,0.15)', width: '28px', height: '28px' }}
//                     onClick={removeProfilePicture} tooltip="Remove photo" tooltipOptions={{ position: 'top' }}
//                   />
//                 </>
//               )}
//             </div>

//             <div style={{ textAlign: 'center', width: '100%' }}>
//               <button
//                 onClick={() => fileUploadRef.current?.getInput()?.click()}
//                 disabled={loading}
//                 style={{ width: '100%', padding: '8px 16px', background: 'linear-gradient(135deg, #0f3d3e 0%, #1a5555 100%)', color: 'white', border: 'none', borderRadius: '8px', fontSize: '0.75rem', fontWeight: '600', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.6 : 1, boxShadow: '0 4px 15px rgba(15, 61, 62, 0.2)', transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
//               >
//                 <i className="pi pi-upload" style={{ fontSize: '0.75rem' }}></i>
//                 Upload Photo
//               </button>
//               <FileUpload
//                 ref={fileUploadRef} name="profilePicture"
//                 accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
//                 maxFileSize={5000000} auto mode="basic" customUpload
//                 uploadHandler={handleProfilePictureSelect} disabled={loading} style={{ display: 'none' }}
//               />
//               <p style={{ marginTop: '6px', fontSize: '0.65rem', color: '#6b7280', lineHeight: '1.3' }}>
//                 PNG, JPG, JPEG, GIF, WebP • Max 5MB
//               </p>
//             </div>
//           </div>

//           {/* Personal Info Fields */}
//           <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
//             <GlobalInputField label="Name of Teacher" required value={formData.name} onChange={(e) => handleInputChange('name', e.target.value)} placeholder="Enter full name" icon="pi pi-user" error={errors.name} />
//             <GlobalDateField label="Date of Birth" required value={formData.dateOfBirth} onChange={(val) => handleInputChange('dateOfBirth', val)} placeholder="Select date" error={errors.dateOfBirth} />
//             <GlobalDropdownField label="Gender" required value={formData.gender} onChange={(val) => handleInputChange('gender', val)} options={genderOptions} placeholder="Select gender" icon="pi pi-mars" error={errors.gender} />
//             <GlobalDropdownField label="Blood Group" value={formData.bloodGroup} onChange={(val) => handleInputChange('bloodGroup', val)} options={bloodGroupOptions} placeholder="Select blood group" icon="pi pi-heart" />
//             <GlobalInputField label="Aadhar Number" required value={formData.aadharNumber} onChange={(e) => handleInputChange('aadharNumber', e.target.value)} placeholder="Enter 12-digit Aadhar" maxLength={12} icon="pi pi-id-card" error={errors.aadharNumber} />
//             <GlobalDropdownField label="Category" required value={formData.category} onChange={(val) => handleInputChange('category', val)} options={categoryOptions} placeholder="Select category" icon="pi pi-tag" error={errors.category} />
//             <GlobalDropdownField label="Marital Status" required value={formData.maritalStatus} onChange={(val) => handleInputChange('maritalStatus', val)} options={maritalStatusOptions} placeholder="Select marital status" icon="pi pi-users" error={errors.maritalStatus} />
//           </div>
//         </div>
//       </div>

//       {/* ── Contact Information ── */}
//       <div style={sectionStyle}>
//         <div style={sectionHeaderStyle}>
//           <div style={iconBoxStyle}><i className="pi pi-phone" style={{ color: '#d4f17e', fontSize: '1.1rem' }}></i></div>
//           <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700', color: '#0f3d3e' }}>Contact Information</h2>
//         </div>
//         <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
//           <GlobalInputField label="Mobile Number" required value={formData.mobileNumber} onChange={(e) => handleInputChange('mobileNumber', e.target.value)} placeholder="Enter mobile number" maxLength={10} icon="pi pi-mobile" error={errors.mobileNumber} />
//           <GlobalInputField label="WhatsApp Number" value={formData.whatsappNumber} onChange={(e) => handleInputChange('whatsappNumber', e.target.value)} placeholder="Enter WhatsApp number" maxLength={10} icon="pi pi-whatsapp" error={errors.whatsappNumber} />
//           <GlobalInputField label="Email ID" type="email" required value={formData.email} onChange={(e) => handleInputChange('email', e.target.value)} placeholder="Enter email" icon="pi pi-envelope" error={errors.email} />
//         </div>
//       </div>

//       {/* ── Employment Details ── */}
//       <div style={sectionStyle}>
//         <div style={sectionHeaderStyle}>
//           <div style={iconBoxStyle}><i className="pi pi-briefcase" style={{ color: '#d4f17e', fontSize: '1.1rem' }}></i></div>
//           <div>
//             <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700', color: '#0f3d3e' }}>Employment Details</h2>
//             <p style={{ margin: '2px 0 0 0', fontSize: '0.75rem', color: '#9ca3af' }}>
//               Employee ID is auto-generated on submit (e.g. HCI-JK-EMP-2026-0001)
//             </p>
//           </div>
//         </div>
//         <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
//           {/* Employee ID — read-only */}
//           <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
//             <label style={{ fontSize: '0.85rem', fontWeight: '600', color: '#374151' }}>Employee ID</label>
//             <div style={{ padding: '10px 14px', background: '#f9fafb', borderRadius: '8px', border: '1px dashed #d1d5db', display: 'flex', alignItems: 'center', gap: '8px' }}>
//               <i className="pi pi-id-card" style={{ color: '#9ca3af', fontSize: '0.9rem' }}></i>
//               <span style={{ fontSize: '0.82rem', color: '#9ca3af', fontStyle: 'italic' }}>
//                 {generatedEmployeeId
//                   ? <strong style={{ color: '#0f3d3e', fontFamily: 'monospace', fontStyle: 'normal' }}>{generatedEmployeeId}</strong>
//                   : 'Auto-generated after submit'}
//               </span>
//             </div>
//           </div>
//           <GlobalInputField label="Qualification" required value={formData.qualification} onChange={(e) => handleInputChange('qualification', e.target.value)} placeholder="e.g., B.Ed, M.Ed, B.Sc" icon="pi pi-graduation-cap" error={errors.qualification} />
//           <GlobalDateField label="Date of Joining" required value={formData.dateOfJoining} onChange={(val) => handleInputChange('dateOfJoining', val)} placeholder="Select date" error={errors.dateOfJoining} />
//           <GlobalInputField label="Experience" value={formData.experience} onChange={(e) => handleInputChange('experience', e.target.value)} placeholder="e.g., 5 years, 10 years" icon="pi pi-chart-line" />
//         </div>
//       </div>

//       {/* ── Spouse & Emergency Contact ── */}
//       <div style={sectionStyle}>
//         <div style={sectionHeaderStyle}>
//           <div style={iconBoxStyle}><i className="pi pi-users" style={{ color: '#d4f17e', fontSize: '1.1rem' }}></i></div>
//           <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700', color: '#0f3d3e' }}>Spouse & Emergency Contact</h2>
//         </div>
//         <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
//           <GlobalInputField label="Spouse Name" value={formData.spouseName} onChange={(e) => handleInputChange('spouseName', e.target.value)} placeholder="Enter spouse name (if married)" icon="pi pi-user" />
//           <GlobalInputField label="Spouse Contact" value={formData.spouseContact} onChange={(e) => handleInputChange('spouseContact', e.target.value)} placeholder="Enter spouse contact" maxLength={10} icon="pi pi-phone" error={errors.spouseContact} />
//           <GlobalInputField label="Emergency Contact Name" value={formData.emergencyContactName} onChange={(e) => handleInputChange('emergencyContactName', e.target.value)} placeholder="Enter emergency contact name" icon="pi pi-user" />
//           <GlobalInputField label="Emergency Contact Number" value={formData.emergencyContactNumber} onChange={(e) => handleInputChange('emergencyContactNumber', e.target.value)} placeholder="Enter emergency contact number" maxLength={10} icon="pi pi-phone" error={errors.emergencyContactNumber} />
//           <GlobalDropdownField label="Relation" value={formData.emergencyContactRelation} onChange={(val) => handleInputChange('emergencyContactRelation', val)} options={relationOptions} placeholder="Select relation" icon="pi pi-users" />
//         </div>
//       </div>

//       {/* ── Address ── */}
//       <div style={sectionStyle}>
//         <div style={sectionHeaderStyle}>
//           <div style={iconBoxStyle}><i className="pi pi-map-marker" style={{ color: '#d4f17e', fontSize: '1.1rem' }}></i></div>
//           <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700', color: '#0f3d3e' }}>Address</h2>
//         </div>
//         <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
          
//             <GlobalDropdownField
//             label="State/UT"
//             required
//             value={formData.state}
//             onChange={(val) => handleInputChange('state', val)}
//             options={INDIAN_STATES}
//             placeholder="Select state/UT"
//             icon="pi pi-globe"
//             error={errors.state}
//             filter
//           />
//           <GlobalInputField label="District" required value={formData.district} onChange={(e) => handleInputChange('district', e.target.value)} placeholder="Enter district" icon="pi pi-map-marker" error={errors.district} />
//           <GlobalInputField label="Tehsil" value={formData.tehsil} onChange={(e) => handleInputChange('tehsil', e.target.value)} placeholder="Enter tehsil" icon="pi pi-map" />
//           <GlobalInputField label="Ward No" value={formData.wardNo} onChange={(e) => handleInputChange('wardNo', e.target.value)} placeholder="Enter ward number" icon="pi pi-map" />
//           <GlobalInputField label="Village/Town" value={formData.villageTown} onChange={(e) => handleInputChange('villageTown', e.target.value)} placeholder="Enter village/town" icon="pi pi-home" />
          
          
//           {/* ── State dropdown replacing plain text input ── */}
          
//           <GlobalInputField label="Pin Code" required value={formData.pinCode} onChange={(e) => handleInputChange('pinCode', e.target.value)} placeholder="Enter 6-digit pin code" maxLength={6} icon="pi pi-inbox" error={errors.pinCode} />
//         </div>
//       </div>

//       {/* ── Action Buttons ── */}
//       <div style={{ background: 'white', borderRadius: '16px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', border: '1px solid #f0f0f0', display: 'flex', gap: '12px', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap' }}>
//         <GlobalButton label={loading ? 'Adding Teacher...' : 'Add Teacher'} variant="primary" icon={loading ? 'pi pi-spin pi-spinner' : 'pi pi-check'} onClick={handleSubmit} disabled={loading} loading={loading} size="large" />
//         <GlobalButton label="Cancel" variant="outline" icon="pi pi-times" onClick={handleCancel} disabled={loading} size="large" />
//       </div>
//     </div>
//   );
// };

// export default AddTeacher;